/**
 * The application fields a company can map to their CRM, and the only ones the configuration page
 * offers. A saved mapping is keyed by `key`, so a `label` is only ever what a person reads.
 */

/**
 * Where the account and the owner point, as one CRM's own metadata names the records they point
 * at. The form offers a CRM field for one of these only when the field reports the same name, and
 * the integrations service checks the same thing again when the mapping is saved.
 *
 * It cannot be one shared value: a push binds a reference to whatever the CRM calls the thing it
 * points at, so the name has to be the CRM's own. Each CRM declares its pair in `crmProviders`.
 */
export type LookupTargets = { account: string; owner: string };

export type CrmControlKind =
  | "string"
  | "textarea"
  | "number"
  | "date"
  | "account"
  | "owner"
  | "picklist";

/**
 * Which Stotles object is being pushed. Leads and bids both become the same CRM object — the
 * one the field mapping names — but they carry different data, so each offers its own fields.
 */
export type CrmRecordKind = "lead" | "bid" | "contact";

const LEAD_AND_BID: CrmRecordKind[] = ["lead", "bid"];
const EVERY_KIND: CrmRecordKind[] = ["lead", "bid", "contact"];
const LEAD_AND_CONTACT: CrmRecordKind[] = ["lead", "contact"];

/** The record data available to seed the form. */
export type CrmRecordSeed = {
  title?: string;
  description?: string;
  value?: number;
  expiryDate?: string;
  clarificationDeadline?: string;
  submissionDate?: string;
  startDate?: string;
  awardDate?: string;
  salutation?: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  jobFunction?: string;
  phone?: string;
  email?: string;
  seniority?: string;
  buyerName?: string;
};

export type RecordFieldSpec = {
  /**
   * The key this field takes in the generated JSON schema. Also decides which JSON-Forms renderer
   * picks it up: the account and owner pickers are selected by the scope ending in "account" and
   * "owner" respectively.
   */
  key: string;
  label: string;
  control: CrmControlKind;
  /** The records that offer this field. A mapped field the record has no data for is not one. */
  kinds: CrmRecordKind[];
  /** Which piece of record data pre-fills the field, if any. */
  seed?: keyof CrmRecordSeed;
  /** Describes the person a record is about, so it is offered only where the object holds one. */
  aboutThePerson?: boolean;
  /**
   * For a lookup field, what the CRM calls the records it points at. Filled in per CRM rather than
   * declared here — each names the same thing differently — so a spec only carries it once it has
   * been resolved for the connected CRM.
   */
  lookupEntitySet?: string;
};

/**
 * A field describing the person a record is about. Each seeds from the record key of its own name,
 * which is what keeps the two from drifting apart.
 */
function personField(key: keyof CrmRecordSeed, label: string): RecordFieldSpec {
  return {
    key,
    label,
    control: "string",
    kinds: LEAD_AND_CONTACT,
    aboutThePerson: true,
    seed: key,
  };
}

export const RECORD_FIELD_SPECS: RecordFieldSpec[] = [
  {
    key: "title",
    label: "Name",
    control: "string",
    kinds: LEAD_AND_BID,
    seed: "title",
  },
  {
    key: "description",
    label: "Description",
    control: "textarea",
    kinds: LEAD_AND_BID,
    seed: "description",
  },
  { key: "value", label: "Value", control: "number", kinds: LEAD_AND_BID, seed: "value" },
  {
    key: "expiryDate",
    label: "Expiry date",
    control: "date",
    kinds: ["lead"],
    seed: "expiryDate",
  },
  {
    key: "clarificationDeadline",
    label: "Clarification deadline",
    control: "date",
    kinds: ["bid"],
    seed: "clarificationDeadline",
  },
  {
    key: "submissionDate",
    label: "Submission date",
    control: "date",
    kinds: ["bid"],
    seed: "submissionDate",
  },
  { key: "startDate", label: "Start date", control: "date", kinds: ["bid"], seed: "startDate" },
  { key: "awardDate", label: "Award date", control: "date", kinds: ["bid"], seed: "awardDate" },
  { key: "stage", label: "Stage", control: "picklist", kinds: LEAD_AND_BID },
  personField("salutation", "Title"),
  personField("firstName", "First name"),
  personField("lastName", "Last name"),
  personField("jobTitle", "Job title"),
  personField("jobFunction", "Job function"),
  personField("phone", "Phone number"),
  personField("email", "Email"),
  personField("seniority", "Seniority"),
  /**
   * The organisation the person works for, as a name rather than a reference.
   *
   * `account` already points at one, but only where the object has a reference to point with. The
   * CRM objects that hold an unqualified person mostly do not: a Salesforce Lead keeps its employer
   * in `Company`, free text, because it is not attached to an account yet. Without this the one
   * field such a create is refused without is the one the mapping cannot reach.
   */
  personField("buyerName", "Buyer name"),
  // A bid's buyer and a contact's employer are both an account in the CRM, so they are this one
  // field under the names each record uses for it: the picker is chosen by the schema key, and only
  // a key ending in "account" reaches it. It has to be mapped to a lookup — a text field is refused,
  // and in Dynamics a contact's is `parentcustomerid` rather than `accountid`.
  {
    key: "account",
    // "Buyer" to the user; the key stays `account` because saved mappings are stored under it.
    label: "Buyer",
    control: "account",
    kinds: EVERY_KIND,
  },
  { key: "owner", label: "Owner", control: "owner", kinds: EVERY_KIND },
];

/**
 * Fields holding a reference to another CRM record rather than a scalar, searched live through the
 * integrations service and sent as lookups.
 */
export const ACCOUNT_FIELD_KEY = "account";
export const OWNER_FIELD_KEY = "owner";

/** The reference-carrying fields, which are the only ones a CRM has to name a target for. */
const LOOKUP_KEYS: Record<string, keyof LookupTargets> = {
  account: "account",
  owner: "owner",
};

/**
 * Fields whose value has to be one the CRM already knows, so the form asks the integrations service
 * what they are and offers a dropdown.
 */
export const PICKLIST_FIELD_KEYS: string[] = RECORD_FIELD_SPECS.filter(
  (spec) => spec.control === "picklist",
).map((spec) => spec.key);

/**
 * What the CRM calls the records a field points at, or undefined when the field holds a scalar.
 * Doubles as the test for "is this a lookup", so the two cannot disagree.
 */
export function lookupEntitySetFor(key: string, targets: LookupTargets): string | undefined {
  const lookup = LOOKUP_KEYS[key];
  return lookup ? targets[lookup] : undefined;
}

/** The application fields as they apply to one CRM, each reference pointed at what that CRM calls it. */
export function resolveLookupTargets(
  specs: RecordFieldSpec[],
  targets: LookupTargets,
): RecordFieldSpec[] {
  return specs.map((spec) => {
    const lookupEntitySet = lookupEntitySetFor(spec.key, targets);
    return lookupEntitySet ? { ...spec, lookupEntitySet } : spec;
  });
}

/**
 * Labels are typed by hand in the Portal Editor, so compare them case-insensitively and ignore
 * surrounding whitespace rather than failing to find a field over a stray space. Nothing here
 * matches on labels any more; HubSpot, which still reads its mapping from the Portal, does.
 */
export function normaliseLabel(label: string): string {
  return label.trim().toLowerCase();
}

/** How each record is named in copy and analytics. Lowercased where the sentence needs it. */
export const RECORD_LABELS: Record<CrmRecordKind, string> = {
  lead: "Lead",
  bid: "Bid",
  contact: "Contact",
};

/**
 * The fields describing the person a record is about, rather than the record itself.
 *
 * Named so the send form can refill exactly these when a different person is chosen, and leave
 * everything else as the user left it.
 */
export const PERSON_FIELD_KEYS: string[] = RECORD_FIELD_SPECS.filter(
  (spec) => spec.aboutThePerson,
).map((spec) => spec.key);

/** The contact data a mapping can reach. */
export type CrmContactFields = {
  salutation?: string;
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  jobFunction?: string;
  phone?: string;
  email?: string;
  seniority?: string;
  /** The organisation they work for, by name — what a CRM object holding an unqualified person
   *  keeps instead of a reference to an account. */
  buyerName?: string;
};

/**
 * Someone a record can be sent as, which is one of the contacts saved to it.
 *
 * A CRM object that holds an unqualified person needs a person's details as well as the record's,
 * and a lead has no surname of its own — so they come from whoever is chosen here.
 */
export type CrmPerson = CrmContactFields & {
  id: string;
  /** What the chooser shows. */
  name: string;
};

/**
 * The person's own details. Taken from a contact for a record that is not already one, and from
 * the record itself for a contact — the same fields either way, so one place names them.
 */
export function personSeed(person: CrmContactFields | undefined): CrmRecordSeed {
  if (!person) return {};
  return {
    salutation: person.salutation,
    firstName: person.firstName,
    lastName: person.lastName,
    jobTitle: person.jobTitle,
    jobFunction: person.jobFunction,
    phone: person.phone,
    email: person.email,
    seniority: person.seniority,
    buyerName: person.buyerName,
  };
}
